import { useState } from 'react'

function usePersistedState<T>(
  key: string,
  initialValue: string | number | boolean | Array<T>,
  storageOption: 'localStorage' | 'sessionStorage' = 'localStorage',
) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window[storageOption].getItem(key)
      // If the item doesn't already exist, we will create a new localStorage value
      // and use the provided key and initialValue
      if (!item) {
        window[storageOption].setItem(key, JSON.stringify(initialValue))
      }
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: string | ((value: string) => string)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      // Save to local storage
      window[storageOption].setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      // A more advanced implementation would handle the error case
    }
  }

  return [storedValue, setValue]
}

export { usePersistedState }
